import React, { useState } from 'react';
import { CodeBracketIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import './Projects.css';

// ProjectCard Component
function ProjectCard({ project, isActive, onClick }) {
  return (
    <div
      className={`project-card ${isActive ? 'active' : ''}`}
      onClick={onClick}
    >
      <div className="project-image-container">
        <img
          src={project.image}
          alt={project.title}
          className="project-image"
        />
        <div className="project-overlay">
          <div className={`project-status ${project.status === 'Completed' ? 'status-completed' :
              project.status === 'Active / Proprietary' ? 'status-active' : ''
            }`}>
            {project.status || 'In Progress'}
          </div>
        </div>
      </div>

      <div className="project-content">
        <h3 className="project-title">{project.title}</h3>

        {project.subtitle && (
          <p className="project-subtitle">{project.subtitle}</p>
        )}

        <p className="project-description">{project.description}</p>

        <button className="expand-btn">
          {isActive ? 'Collapse ▲' : 'Expand ▼'}
        </button>

        {isActive && (
          <>
            {project.highlights && project.highlights.length > 0 && (
              <div className="project-highlights">
                <div className="highlights-label">Key Features</div>
                <div className="highlights-list">
                  {project.highlights.map((highlight, index) => (
                    <div key={index} className="highlight-item">
                      <span className="highlight-icon">{`>`}</span>
                      {highlight}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="project-tech">
              <div className="tech-label">Technologies</div>
              <div className="tech-tags">
                {project.tech.map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="project-links">
          {project.codeLink && project.codeLink !== '#' && (
            <a
              href={project.codeLink}
              className="project-link code-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="link-icon"><CodeBracketIcon width={16} height={16} strokeWidth={2.5} /></span>
              Check This
            </a>
          )}
          {project.demoLink && project.demoLink !== '#' && (
            <a
              href={project.demoLink}
              className="project-link demo-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="link-icon"><ArrowTopRightOnSquareIcon width={16} height={16} strokeWidth={2.5} /></span>
              Check This
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Projects() {
  const [activeProject, setActiveProject] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const projects = [
    // ── Professional ──────────────────────────────────────────────────────────
    {
      id: 1,
      category: 'professional',
      title: 'WhereOne',
      subtitle: 'Cloud-Native Geospatial Platform',
      description: 'WhereSoft\'s flagship web-based, cloud-native geospatial platform featuring agentic AI for prompt-based GIS processing. I contributed to training and deploying deep learning models (YOLO, Mask R-CNN, SAM) for object detection and segmentation tasks including building footprint extraction and pond boundary segmentation. Also built LLM-based AI agents using Mistral, Gemini, LangChain, vector databases, and embedding models.',
      image: '/images/whereone.jpg',
      tech: ['Python', 'YOLO', 'Mask R-CNN', 'SAM', 'FastAPI', 'LangChain', 'Qdrant', 'MinIO', 'ArcPy', 'GDAL'],
      status: 'Active / Proprietary',
      highlights: [
        'Deep learning models for building footprint & pond boundary extraction',
        'LLM-based AI agents with local RAG (Qdrant + MinIO + Redis)',
        'FastAPI backend with full API design and endpoint testing',
        'Supports NVIDIA NIM, Google Gemini, and local Ollama LLMs',
      ],
      codeLink: '#',
      demoLink: 'https://www.wheresoft.in/',
    },
    // ── GenAI / Tools ─────────────────────────────────────────────────────────
    {
      id: 2,
      category: 'geoai',
      title: 'LabelSense',
      subtitle: 'Desktop Image Annotation Application',
      description: 'A user-friendly desktop application built for creating and validating YOLO training datasets. LabelSense provides an intuitive image annotation interface purpose-built for the deep learning model training workflow, making it easy to label objects across large image sets.',
      image: 'https://github.com/Rahim-Biswas/LabelSense/raw/main/LabelSense.png',
      tech: ['Python', 'PyQt5', 'YOLO', 'Computer Vision', 'Image Annotation'],
      status: 'Completed',
      highlights: [
        'Intuitive GUI for bounding box and polygon annotation',
        'Purpose-built for YOLO training dataset creation',
        'Dataset validation and quality-check tools',
        'Export to YOLO-compatible format',
      ],
      codeLink: 'https://github.com/Rahim-Biswas/LabelSense.git',
      demoLink: '#',
    },
    // ── Academic ──────────────────────────────────────────────────────────────
    {
      id: 4,
      category: 'academic',
      title: 'Landslide Susceptibility Mapping',
      subtitle: 'ML-Based Risk Assessment — M.Sc. Project',
      description: 'Academic research project for M.Sc. Geoinformatics at BVIEER, Pune. Performed landslide susceptibility mapping and risk assessment using Random Forest and SVM classifiers on geospatial data from ArcGIS Pro and Google Earth Engine. Produced detailed susceptibility zonation maps for hazard management.',
      image: '/images/thesis.png',
      tech: ['Python', 'Random Forest', 'SVM', 'ArcGIS Pro', 'Google Earth Engine', 'Remote Sensing'],
      status: 'Completed',
      highlights: [
        'Random Forest & SVM classifier comparison',
        'Multi-criteria landslide susceptibility zonation',
        'Google Earth Engine for satellite data acquisition',
        'ArcGIS Pro for final cartographic output',
      ],
      codeLink: '#',
      demoLink: 'https://drive.google.com/file/d/1QEp35j3wQHLx3WFbylVrq2xwv3Vka7Ua/view?usp=sharing',
    },
  ];

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'professional', label: 'Professional' },
    { id: 'geoai', label: 'CV / GIS Tools' },
    { id: 'academic', label: 'Academic' },
  ];

  const filtered = activeCategory === 'all'
    ? projects
    : projects.filter(p => p.category === activeCategory);

  return (
    <section className="projects">
      <div className="projects-container">
        <div className="projects-header">
          <h1>My Works</h1>
          <p>Projects crafted with code, maps, creativity and 💜</p>
          <div className="header-decoration"></div>
        </div>

        {/* Category Filter Tabs */}
        <div className="projects-filter">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => {
                setActiveCategory(cat.id);
                setActiveProject(null);
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="projects-grid">
          {filtered.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isActive={activeProject === project.id}
              onClick={() => setActiveProject(project.id === activeProject ? null : project.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Projects;